import { useState, type FormEvent } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { Pencil } from 'lucide-react';
import { updateCustomer } from '../api/memberClient';
import { SectionCard } from './SectionCard';

interface MemberInfoProps {
  customerId: string;
  name: string;
  phone: string | null;
  /** 不再於會員資訊區顯示，僅保留型別以相容呼叫端 */
  lineId?: string | null;
  birthday: string | null;
  createdAt: string;
  onUpdated?: () => void;
}

export function MemberInfo({
  customerId,
  name,
  phone,
  birthday,
  createdAt,
  onUpdated,
}: MemberInfoProps) {
  const [editing, setEditing] = useState(false);
  const [editPhone, setEditPhone] = useState(phone || '');
  const [editBirthday, setEditBirthday] = useState(birthday || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createdAtTaipei = formatInTimeZone(
    new Date(createdAt),
    'Asia/Taipei',
    'yyyy/MM/dd HH:mm'
  );

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const phoneVal = editPhone.trim() || null;
    const birthdayVal = editBirthday.trim() || null;
    if (phoneVal && !/^09\d{8}$/.test(phoneVal)) {
      setError('請輸入有效的手機號碼（09 開頭 10 碼）');
      return;
    }
    setLoading(true);
    try {
      await updateCustomer(customerId, {
        phone: phoneVal,
        birthday: birthdayVal,
      });
      onUpdated?.();
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditPhone(phone || '');
    setEditBirthday(birthday || '');
    setError(null);
    setEditing(false);
  };

  const editAction =
    !editing && onUpdated ? (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
      >
        <Pencil className="w-4 h-4" />
        編輯
      </button>
    ) : undefined;

  return (
    <SectionCard title="基本資料" action={editAction}>
      {editing ? (
        <form onSubmit={handleSave} className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 w-24 flex-shrink-0">手機</span>
            <input
              type="tel"
              value={editPhone}
              onChange={e =>
                setEditPhone(e.target.value.replace(/\D/g, '').slice(0, 10))
              }
              placeholder="0912345678"
              className="flex-1 px-3 py-2 border rounded-md"
              disabled={loading}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 w-24 flex-shrink-0">生日</span>
            <input
              type="date"
              value={editBirthday}
              onChange={e => setEditBirthday(e.target.value)}
              className="flex-1 px-3 py-2 border rounded-md"
              disabled={loading}
            />
            <p className="text-xs text-gray-500">選填，用於生日優惠</p>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
            >
              {loading ? '儲存中...' : '儲存'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="px-4 py-2 border rounded-md text-sm"
            >
              取消
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center">
            <span className="text-gray-600 w-24 flex-shrink-0">姓名</span>
            <span className="text-gray-900 font-medium">{name}</span>
          </div>
          <div className="flex items-center">
            <span className="text-gray-600 w-24 flex-shrink-0">手機</span>
            <span className="text-gray-900">{phone || '未設定'}</span>
          </div>
          <div className="flex items-center">
            <span className="text-gray-600 w-24 flex-shrink-0">生日</span>
            <span className="text-gray-900">{birthday || '未設定'}</span>
          </div>
          <div className="flex items-center">
            <span className="text-gray-600 w-24 flex-shrink-0">註冊時間</span>
            <span className="text-gray-900">{createdAtTaipei}</span>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
